/**
 * MiniAudioPlayer - Compact inline audio player for All Scripts panel
 *
 * Features:
 * - Play/pause button
 * - Progress bar with seeking
 * - Time display (current / total)
 * - Lazy loading: reads the take's presigned URL only when play is clicked
 */

import * as React from 'react';
import { useState, useRef, useCallback, useEffect } from 'react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { Play, Pause, Loader2 } from 'lucide-react';
import { useNarrationAudioSrc } from '@/hooks/useNarrationAudioSrc';

interface MiniAudioPlayerProps {
  slideId: string;
  /** Current audio version — a re-narrated slide plays its new take. */
  version?: number | null;
  audioDuration?: number | null;
  isPlaying: boolean;
  onPlayChange: (playing: boolean) => void;
  className?: string;
}

// Format time as m:ss
const formatTime = (seconds: number): string => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${String(secs).padStart(2, '0')}`;
};

export const MiniAudioPlayer: React.FC<MiniAudioPlayerProps> = ({
  slideId,
  version,
  audioDuration,
  isPlaying,
  onPlayChange,
  className,
}) => {
  const audioRef = useRef<HTMLAudioElement>(null);
  const [requested, setRequested] = useState(false);
  // Play was pressed and the audio has not loaded yet
  const [waiting, setWaiting] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(audioDuration || 0);
  const narration = useNarrationAudioSrc(requested ? slideId : null, version);
  const { onError: onNarrationError } = narration;
  const isLoading = waiting && !narration.error;

  // A new slide or take (e.g. after a re-narrate) is read again on the next play.
  useEffect(() => {
    setRequested(false);
    setWaiting(false);
    setCurrentTime(0);
  }, [slideId, version]);

  const loadAndPlay = useCallback(() => {
    if (narration.src) {
      audioRef.current?.play();
      onPlayChange(true);
      return;
    }
    // Play starts in onLoadedMetadata once the URL is read and the audio loads
    setWaiting(true);
    setRequested(true);
  }, [narration.src, onPlayChange]);

  // Play when audio is loaded
  const handleLoadedMetadata = useCallback(() => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      setWaiting(false);
      audioRef.current.play();
      onPlayChange(true);
    }
  }, [onPlayChange]);

  // Handle play/pause toggle
  const handleTogglePlay = useCallback(() => {
    if (isPlaying) {
      audioRef.current?.pause();
      onPlayChange(false);
    } else {
      loadAndPlay();
    }
  }, [isPlaying, loadAndPlay, onPlayChange]);

  // Stop playback when isPlaying becomes false from parent
  useEffect(() => {
    if (!isPlaying && audioRef.current && !audioRef.current.paused) {
      audioRef.current.pause();
    }
  }, [isPlaying]);

  // Handle time update
  const handleTimeUpdate = useCallback(() => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  }, []);

  // Handle audio ended
  const handleEnded = useCallback(() => {
    onPlayChange(false);
    setCurrentTime(0);
    if (audioRef.current) {
      audioRef.current.currentTime = 0;
    }
  }, [onPlayChange]);

  // Handle seek
  const handleSeek = useCallback((value: number[]) => {
    const newTime = value[0];
    setCurrentTime(newTime);
    if (audioRef.current) {
      audioRef.current.currentTime = newTime;
    }
  }, []);

  // An expired URL is read again and playback restarts in onLoadedMetadata; anything else shows the error
  const handleError = useCallback(() => {
    onPlayChange(false);
    setWaiting(true);
    onNarrationError();
  }, [onPlayChange, onNarrationError]);

  const displayDuration = duration || audioDuration || 0;

  return (
    <div className={cn('flex items-center gap-2', className)}>
      {/* Hidden audio element */}
      {narration.src && (
        <audio
          ref={audioRef}
          src={narration.src}
          onLoadedMetadata={handleLoadedMetadata}
          onTimeUpdate={handleTimeUpdate}
          onEnded={handleEnded}
          onError={handleError}
          preload="metadata"
        />
      )}

      {/* Play/Pause button */}
      <Button
        variant="ghost"
        size="sm"
        className="h-6 w-6 p-0 flex-shrink-0"
        onClick={handleTogglePlay}
        disabled={isLoading || !!narration.error}
      >
        {isLoading ? (
          <Loader2 className="h-3.5 w-3.5 animate-spin" />
        ) : isPlaying ? (
          <Pause className="h-3.5 w-3.5" />
        ) : (
          <Play className="h-3.5 w-3.5" />
        )}
      </Button>

      {/* Progress bar */}
      <div className="flex-1 min-w-0">
        <Slider
          value={[currentTime]}
          max={displayDuration || 1}
          step={0.1}
          onValueChange={handleSeek}
          className="cursor-pointer"
          disabled={!narration.src || isLoading}
        />
      </div>

      {/* Time display */}
      <span className="text-[10px] text-muted-foreground font-mono flex-shrink-0 w-[70px] text-right">
        {formatTime(currentTime)} / {formatTime(displayDuration)}
      </span>
    </div>
  );
};

export default MiniAudioPlayer;
